import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, FileText, Search } from "lucide-react";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import DesignPartnerDialog from "../components/argus/DesignPartnerDialog";
import { Button } from "../components/ui/button";

const PILLARS = [
  {
    Icon: Search,
    title: "Three scanners. One inbox.",
    body: "SAST, DAST, and SCA share one finding store, one risk score, one fix-it panel. No more spreadsheet triage across three vendor consoles.",
  },
  {
    Icon: ShieldCheck,
    title: "Evidence on every finding.",
    body: "Raw HTTP request and response. Code snippet with line highlight. Dependency path + CVE record. No black box. Ever.",
  },
  {
    Icon: Zap,
    title: "Built for speed.",
    body: "First finding in 90 seconds after signup. Background scans with live progress. CI-friendly API tokens.",
  },
  {
    Icon: FileText,
    title: "Built for auditors.",
    body: "Tamper-evident audit log. Encrypted evidence. PDF reports that survive a SOC 2 walkthrough.",
  },
];

const COMPARISON = [
  { row: "Pricing", argus: "$0 free · $49 Pro · Enterprise on ask", legacy: "Six-figure annual" },
  { row: "Time to first finding", argus: "< 90 seconds", legacy: "Weeks" },
  { row: "Evidence per finding", argus: "Raw request / code / CVE", legacy: "PDF summary" },
  { row: "SAST + DAST + SCA in one tool", argus: "Yes", legacy: "Three SKUs, three consoles" },
  { row: "Domain-verification gated DAST", argus: "Hard technical control", legacy: "Honor system" },
  { row: "API-first", argus: "Every screen reads / writes /api/v1", legacy: "API as an afterthought" },
];

export default function WhyArgus() {
  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col">
      <MarketingHeader />

      <section className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <p className="text-xs uppercase tracking-wider text-gold mb-3 font-semibold">Why ArgusSecure</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight max-w-3xl" data-testid="why-title">
          The grown-up alternative to legacy AppSec.
        </h1>
        <p className="mt-6 text-lg text-ink/70 max-w-2xl">
          You don't need another six-figure platform with a 90-day onboarding. You need findings, evidence, and a fix.
          That's what ArgusSecure ships.
        </p>
      </section>

      {/* Pillars */}
      <section className="border-y border-line bg-panel">
        <div className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-8">
          {PILLARS.map(({ Icon, title, body }) => (
            <div key={title} data-testid={`why-pillar-${title.toLowerCase().split(" ")[0]}`}>
              <Icon size={26} className="text-gold mb-3" strokeWidth={2.25} />
              <h3 className="font-display text-2xl mb-2">{title}</h3>
              <p className="text-ink/70">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison table */}
      <section className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-4xl mb-2">ArgusSecure vs the usual suspects.</h2>
        <p className="text-ink/60 mb-8">Side-by-side, the unfair fight you actually want.</p>
        <div className="rounded-xl border border-line bg-panel overflow-hidden" data-testid="comparison-table">
          <table className="w-full text-sm">
            <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 w-1/3"> </th>
                <th className="text-left px-5 py-3 text-gold">ArgusSecure</th>
                <th className="text-left px-5 py-3">Legacy enterprise AppSec</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((r) => (
                <tr key={r.row} className="border-t border-line">
                  <td className="px-5 py-4 text-ink/60">{r.row}</td>
                  <td className="px-5 py-4 font-medium">{r.argus}</td>
                  <td className="px-5 py-4 text-ink/60">{r.legacy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Customer stories — coming soon. No fabricated testimonials per FTC 16 CFR §255. */}
      <section className="bg-panel border-y border-line">
        <div className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16" data-testid="customer-stories-coming-soon">
          <p className="text-xs uppercase tracking-wider text-gold mb-2 font-semibold">Customer stories</p>
          <h2 className="font-display text-4xl mb-4">Coming soon.</h2>
          <p className="text-ink/70 text-lg max-w-2xl">
            We're onboarding our first cohort of design partners now. Want to be quoted here?{" "}
            <DesignPartnerDialog
              source="why-customer-stories"
              trigger={
                <button
                  type="button"
                  className="text-gold hover:text-gold2 underline underline-offset-2"
                  data-testid="why-talk-to-security"
                >
                  Talk to Security →
                </button>
              }
            />
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-panel border-t border-line">
        <div className="mx-auto max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-display text-4xl sm:text-5xl mb-4">Find it before they do.</h2>
          <p className="text-ink/60 mb-6">Free for one project. No card. No demo gauntlet.</p>
          <Link to="/signup">
            <Button className="bg-gold text-paper hover:bg-gold2 h-12 px-7 font-semibold text-base" data-testid="why-cta">
              Start My Free Scan <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
